import { useShowSnackbar } from "@octopusdeploy/design-system-components";
import { useMutation } from "@octopusdeploy/octopus-react-client";
import type { GitRef, ProjectTemplateResource, PublishProjectTemplateCommand, Repository, ShareProjectTemplateToManySpacesCommand } from "@octopusdeploy/octopus-server-client";
import type { ReleaseType } from "~/areas/platformHub/processTemplates/PublishTemplateDialog";
import { ShareTemplateSpacesType } from "~/areas/platformHub/processTemplates/ShareTemplateDialog";
import { createSnackbarMessage } from "~/areas/platformHub/processTemplates/TemplateParametersTable";
import { useProjectTemplateStatus } from "~/areas/platformHub/projectTemplates/ProjectTemplateStatus/index";
import { useProjectTemplateStatusContext } from "~/areas/platformHub/projectTemplates/ProjectTemplateStatusContext";
import { ActionCompleted } from "~/areas/platformHub/projectTemplates/ProjectTemplateWizardDialog";
import type { CommitMessageWithDetails } from "~/areas/projects/components/VersionControl/CommitMessageWithDetails";
import useLocalStorage from "~/hooks/useLocalStorage";

export interface CommitParams {
    commitMessage?: CommitMessageWithDetails;
}

export interface PublishParams {
    versionToPublish: string;
    isPreRelease: boolean;
    releaseType: ReleaseType;
    releaseNotes: string;
}

export interface ShareParams {
    shareType: ShareTemplateSpacesType;
    selectedSpaces: string[];
}

export interface SaveProjectTemplateParams {
    commitParams?: CommitParams;
    publishParams?: PublishParams;
    shareParams?: ShareParams;
}

interface UseSaveProjectTemplateProps {
    projectTemplate: ProjectTemplateResource;
    gitRef: GitRef;
    commitHandler: ((commitMessage: string) => Promise<void>) | undefined;
    onClose: () => void;
    refetchPublishedVersions: () => void;
    refetchIsTemplateShared: () => void;
    setShowInitialSuccessCallout: (value: boolean) => void;
}

export function useSaveProjectTemplate({ projectTemplate, gitRef, commitHandler, onClose, refetchPublishedVersions, refetchIsTemplateShared, setShowInitialSuccessCallout }: UseSaveProjectTemplateProps) {
    const showSnackbar = useShowSnackbar();

    const [dismissed] = useLocalStorage(`Octopus.ProjectTemplate.${projectTemplate.Slug}.Status.Dismissed`, false);
    const projectTemplateStatus = useProjectTemplateStatusContext();
    const { refetch: refetchProjectTemplateStatus } = useProjectTemplateStatus(projectTemplateStatus.dispatch, projectTemplate.Slug, dismissed, gitRef);

    const { execute } = useMutation({
        name: "Save project template",
        action: async (repository: Repository, params: SaveProjectTemplateParams) => {
            const actionsCompleted: ActionCompleted[] = [];

            if (params.commitParams && commitHandler) {
                const commitMessage = params.commitParams.commitMessage?.summary || "Update project template";
                await commitHandler(commitMessage);
                actionsCompleted.push(ActionCompleted.Committed);
            }

            if (params.publishParams) {
                const publishCommand: PublishProjectTemplateCommand = {
                    ProjectTemplateSlug: projectTemplate.Slug,
                    GitRef: projectTemplate.GitRef,
                    Version: params.publishParams.versionToPublish,
                    ReleaseNotes: params.publishParams.releaseNotes,
                };
                await repository.ProjectTemplates.publishVersion(publishCommand);
                actionsCompleted.push(ActionCompleted.Published);
            }

            if (params.shareParams) {
                const shareCommand: ShareProjectTemplateToManySpacesCommand = {
                    ProjectTemplateSlug: projectTemplate.Slug,
                    GitRef: projectTemplate.GitRef,
                    ShareToAllSpaces: params.shareParams.shareType === ShareTemplateSpacesType.ShareAll,
                    IndividuallySharedSpaceIds: params.shareParams.shareType === ShareTemplateSpacesType.ShareIndividual ? params.shareParams.selectedSpaces : [],
                };
                await repository.ProjectTemplates.shareToSpace(shareCommand);
                actionsCompleted.push(ActionCompleted.Shared);
            }

            return actionsCompleted;
        },
        afterComplete: async (_, result) => {
            const snackbarMessage = createSnackbarMessage(result, "project template");
            if (snackbarMessage) {
                showSnackbar(snackbarMessage);
            }

            refetchProjectTemplateStatus();
            if (result.includes(ActionCompleted.Published)) {
                refetchPublishedVersions();
            }
            if (result.includes(ActionCompleted.Committed) && result.includes(ActionCompleted.Published) && result.includes(ActionCompleted.Shared)) {
                setShowInitialSuccessCallout(true);
            }
            if (result.includes(ActionCompleted.Shared)) {
                refetchIsTemplateShared();
            }

            onClose();
        },
    });

    return { executeSave: execute };
}
